import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowRight, Search, Sparkles } from "lucide-react";
import { categories, liveTools, liveToolsByCategory, TOTAL_TOOLS, searchTools } from "@/lib/toolsData";
import { ToolCard } from "@/components/ToolCard";
import { BannerAd } from "@/components/AdSlots";
import { FavoritesBar } from "@/components/FavoritesBar";
import { useSeo } from "@/lib/useSeo";

const FAQS = [
  { q: "Is Kovi Tools free to use?", a: "Yes. Every live tool is completely free with no signup, no watermarks and no daily limits." },
  { q: "Are my files safe?", a: "Most tools run entirely in your browser so files never leave your device. Tools that need a server (like Office conversions) process files temporarily and never store them." },
  { q: "Do I need to install anything?", a: "No. Kovi Tools works in any modern browser on desktop and mobile." },
  { q: "How many tools are there?", a: `We have ${TOTAL_TOOLS} production-ready tools across PDF, converters, calculators and developer categories — every one fully functional, with more added regularly.` },
];

const Home = () => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const all = liveTools();
  const popular = all.slice(0, 8);
  const recent = all.slice(-8).reverse();
  const results = searchTools(q).slice(0, 8);

  useSeo({
    title: `${TOTAL_TOOLS} Free Online Tools — PDF, Converters, Calculators, Developer`,
    description: `Kovi Tools is a fast, free, self-hosted platform with ${TOTAL_TOOLS} production-ready online tools: merge & convert PDFs, convert files, calculators and developer utilities. Private and no signup.`,
    path: "/",
    jsonLd: {
      "@context": "https://schema.org", "@type": "WebSite", name: "Kovi Tools",
      url: typeof window !== "undefined" ? window.location.origin : "",
      potentialAction: { "@type": "SearchAction", target: `${typeof window !== "undefined" ? window.location.origin : ""}/?q={search_term_string}`, "query-input": "required name=search_term_string" },
    },
  });

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-5xl px-4 py-20 text-center sm:px-6 sm:py-28 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-6xl">
              Every online tool you need,<br className="hidden sm:block" /> in <span className="text-primary">one place</span>.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
              Merge and convert PDFs, transform files, crunch numbers and speed up development — free, fast and private.
            </p>

            <div className="relative mx-auto mt-8 max-w-xl">
              <div className="flex items-center rounded-xl border border-border bg-card px-4 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                <Search className="h-5 w-5 text-muted-foreground" />
                <input data-testid="home-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for a tool… e.g. Merge PDF, JSON, EMI" className="w-full bg-transparent px-3 py-4 text-sm outline-none" />
              </div>
              {q && results.length > 0 && (
                <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-border bg-card text-left shadow-lg">
                  {results.map((t) => (
                    <button key={t.slug} data-testid={`home-search-result-${t.slug}`} onClick={() => t.status === "live" ? navigate(`/tool/${t.slug}`) : navigate(`/category/${t.category}`)} className="flex w-full items-center justify-between px-4 py-2.5 hover:bg-muted">
                      <span className="text-sm font-medium">{t.name}</span>
                      {t.status === "soon" && <span className="overline text-[10px] text-muted-foreground">Soon</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <FavoritesBar />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Categories */}
        <section className="py-14">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Browse by category</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((c) => {
              const Icon = c.icon;
              const liveN = liveToolsByCategory(c.id).length;
              return (
                <Link key={c.id} to={`/category/${c.id}`} data-testid={`bento-${c.id}`} className="group rounded-2xl border border-border bg-card p-6 transition-all duration-200 hover:border-primary/40 hover:shadow-lg">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl text-white" style={{ backgroundColor: c.accent }}><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-heading text-lg font-bold">{c.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{c.description}</p>
                  <p className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">{liveN} tools <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></p>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Popular */}
        <section className="pb-14">
          <div className="flex items-end justify-between border-b border-border pb-3">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Popular tools</h2>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {popular.map((t) => <ToolCard key={t.slug} tool={t} />)}
          </div>
        </section>

        <BannerAd className="mb-14" />

        {/* Recently added */}
        <section className="pb-14">
          <div className="flex items-end justify-between border-b border-border pb-3">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Recently added</h2>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {recent.map((t) => <ToolCard key={t.slug} tool={t} />)}
          </div>
        </section>

        {/* FAQ */}
        <section className="pb-20">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Frequently asked questions</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-xl border border-border bg-card p-5">
                <p className="font-semibold">{f.q}</p>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
